import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useThemeContext } from "@/shared/providers/ThemeProvider";

/**
 * The user's chosen appearance:
 *   • "light"  — always light theme regardless of OS preference
 *   • "dark"   — always dark theme regardless of OS preference
 *   • "system" — follow the OS / browser `prefers-color-scheme`
 *
 * `system` is the default for first-time users so the app respects
 * whatever the rest of the user's OS is doing without surprising them.
 */
export type Appearance = "light" | "dark" | "system";

/** The actually-applied theme after resolving "system". Always concrete. */
export type ResolvedAppearance = "light" | "dark";

/**
 * Dark-theme CSS variable values applied as INLINE styles on `<html>`.
 * We can't rely solely on the `.dark { ... }` ruleset in globals.css
 * because the tenant `ThemeProvider.applyTheme` writes light-theme
 * variables as inline styles on `<html>`, and inline styles beat class
 * selectors in CSS specificity. Re-applying the dark values inline at
 * the same scope guarantees they win in dark mode; removing them
 * restores the tenant's light values for light mode.
 *
 * Anchored on the Figma-specified `#1A1A1A` page background (neutral
 * gray, not navy). Surfaces step up in 8 % luminance increments
 * (#1A1A1A → #262626 → #303030).
 *
 * NOT overridden in dark mode (intentionally inherits the light /
 * tenant value — the navbar is part of the brand chrome and stays
 * consistent across themes):
 *   • `--color-header`                 (navbar background — navy)
 *   • `--color-on-primary`             (white icons / text on navbar)
 *   • `--color-on-secondary`           (white text on secondary)
 *   • `--color-sidebar-header`         (sidebar sub-header)
 *   • `--color-sidebar-header-border`
 *
 * Keep this in sync with the `.dark` ruleset in `src/styles/globals.css`.
 */
const DARK_TOKENS: Record<string, string> = {
  "--color-primary":              "59 130 246",   // #3B82F6 — keeps WCAG AA contrast w/ white
  "--color-primary-light":        "30 58 138",    // #1E3A8A
  "--color-secondary":            "129 140 248",  // #818CF8
  "--color-background":           "26 26 26",     // #1A1A1A — Figma spec
  // Dark-mode quality lift (2026-05-11) — see globals.css for the
  // longer rationale. Keep these in lockstep with the `.dark` block
  // there; the runtime JS map wins on hydration.
  //
  // Second-pass premium-feel lift: cool-tinted surface tokens
  // (R<G<B by ~+10) so the dashboard reads as "designed" rather than
  // flat console-grey. Borders bumped again to stay visible against
  // the lifted surface.
  "--color-surface":              "44 46 54",     // #2C2E36 — cool-tinted lift
  "--color-surface-alt":          "52 54 62",     // #34363E — matching tint, hover/selected
  "--color-text":                 "229 229 229",  // #E5E5E5
  "--color-text-muted":           "163 163 163",  // #A3A3A3
  "--color-text-strong":          "250 250 250",  // #FAFAFA
  "--color-text-disabled":        "82 82 82",     // #525252
  "--color-text-subtle":          "115 115 115",  // #737373
  "--color-border":               "69 73 82",     // #454952 — cool-tinted hairlines
  "--color-border-subtle":        "56 60 68",     // #383C44 — quiet but visible
  "--color-border-strong":        "100 105 115", // #646973 — lifted strong border
  "--color-success":              "74 222 128",   // #4ADE80
  "--color-warning":              "245 158 11",   // #F59E0B
  "--color-error":                "248 113 113",  // #F87171
  "--color-info":                 "96 165 250",   // #60A5FA
  "--color-on-semantic":          "26 26 26",     // #1A1A1A
  "--color-online":               "74 222 128",
  "--color-online-bg":            "38 38 38",
  "--color-ring":                 "59 130 246",
  "--color-accordion-arrow":      "163 163 163",
  "--color-reset-button":         "229 229 229",
  "--color-arrow-icon":           "163 163 163",
  "--color-table-header":         "38 38 38",
  "--color-tableBlueColor":       "96 165 250",
  "--color-tabActive":            "38 51 80",
  "--color-activeBorder":         "59 130 246",
  "--color-disabled":             "82 82 82",
};

/** Saved tenant values per CSS variable, captured before dark mode
 * applies, so we can restore them when the user switches back to
 * light. Lives at module scope so it survives provider re-renders. */
const savedTenantTokens: Record<string, string> = {};

/** Apply or remove the dark inline overrides on `<html>`. */
const applyAppearanceTokens = (theme: ResolvedAppearance): void => {
  const root = document.documentElement;
  if (theme === "dark") {
    Object.entries(DARK_TOKENS).forEach(([cssVar, value]) => {
      // Save the tenant's current value (only first time we touch it).
      if (!(cssVar in savedTenantTokens)) {
        savedTenantTokens[cssVar] = root.style.getPropertyValue(cssVar);
      }
      root.style.setProperty(cssVar, value);
    });
  } else {
    // Restore each tenant value (or remove the inline override entirely
    // if the tenant didn't have one).
    Object.keys(DARK_TOKENS).forEach((cssVar) => {
      const original = savedTenantTokens[cssVar];
      if (original) {
        root.style.setProperty(cssVar, original);
      } else {
        root.style.removeProperty(cssVar);
      }
    });
  }
};

interface AppearanceContextValue {
  /** The user's chosen preference (raw, may be "system"). */
  appearance: Appearance;
  /** The currently applied theme (always "light" or "dark"). */
  resolvedAppearance: ResolvedAppearance;
  /** Update the preference and persist it. */
  setAppearance: (next: Appearance) => void;
}

const STORAGE_KEY = "appearance";
const DEFAULT_APPEARANCE: Appearance = "system";

const AppearanceContext = createContext<AppearanceContextValue | undefined>(
  undefined,
);

const isAppearance = (v: unknown): v is Appearance =>
  v === "light" || v === "dark" || v === "system";

const readSavedAppearance = (): Appearance => {
  if (typeof window === "undefined") return DEFAULT_APPEARANCE;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return isAppearance(v) ? v : DEFAULT_APPEARANCE;
  } catch {
    return DEFAULT_APPEARANCE;
  }
};

const getSystemAppearance = (): ResolvedAppearance => {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
};

const resolve = (appearance: Appearance): ResolvedAppearance =>
  appearance === "system" ? getSystemAppearance() : appearance;

interface AppearanceProviderProps {
  children: ReactNode;
}

/**
 * Tracks the user's light / dark / system preference, persists it to
 * localStorage, and toggles a `dark` class on `<html>` so the existing
 * CSS-variable theme tokens (in globals.css `.dark` ruleset) take
 * effect across the app.
 *
 * This lives ALONGSIDE the tenant `ThemeProvider`. Tenant branding sets
 * primary / secondary colors per customer; appearance flips the
 * background / surface / text tokens between light and dark variants.
 */
export const AppearanceProvider: React.FC<AppearanceProviderProps> = ({
  children,
}) => {
  const [appearance, setAppearanceState] = useState<Appearance>(
    readSavedAppearance,
  );
  const [resolvedAppearance, setResolvedAppearance] =
    useState<ResolvedAppearance>(() => resolve(readSavedAppearance()));

  // Watch the tenant theme so we re-apply dark tokens any time the
  // tenant `applyTheme` rewrites the inline styles on `<html>`.
  const { theme: tenantTheme } = useThemeContext();

  // Apply the resolved theme to <html>:
  //  • toggle the `dark` class (so any `dark:*` Tailwind variants work)
  //  • apply / restore the dark CSS variables as INLINE styles (to win
  //    over the tenant ThemeProvider's inline light-theme styles)
  //  • set `color-scheme` so native UI (scrollbars, form inputs, etc.)
  //    uses the right built-in palette
  //
  // Tenant `ThemeProvider` runs `applyTheme` in its own useEffect; on
  // initial mount React fires CHILD effects before PARENT, which means
  // our effect runs FIRST (we're the child) and tenant overwrites our
  // tokens immediately after. To beat this race we also re-apply on
  // the next animation frame — by then the tenant has already written
  // its values, and our re-application wins. Cheap, runs once on mount
  // and again only when our deps actually change.
  useEffect(() => {
    const root = document.documentElement;
    if (resolvedAppearance === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    root.style.colorScheme = resolvedAppearance;
    applyAppearanceTokens(resolvedAppearance);
    // Re-apply on the next frame so we run AFTER tenant ThemeProvider's
    // useEffect (which fires later because it's a parent of ours).
    const rafId = requestAnimationFrame(() => {
      applyAppearanceTokens(resolvedAppearance);
    });
    return () => cancelAnimationFrame(rafId);
  }, [resolvedAppearance, tenantTheme]);

  // Re-resolve when appearance changes.
  useEffect(() => {
    setResolvedAppearance(resolve(appearance));
  }, [appearance]);

  // While appearance === "system", listen for OS-level changes so the
  // app flips theme without a reload.
  useEffect(() => {
    if (appearance !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent): void => {
      setResolvedAppearance(e.matches ? "dark" : "light");
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [appearance]);

  const setAppearance = useCallback((next: Appearance): void => {
    setAppearanceState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* storage unavailable — fall back to in-memory only */
    }
  }, []);

  const value = useMemo<AppearanceContextValue>(
    () => ({ appearance, resolvedAppearance, setAppearance }),
    [appearance, resolvedAppearance, setAppearance],
  );

  return (
    <AppearanceContext.Provider value={value}>
      {children}
    </AppearanceContext.Provider>
  );
};

export const useAppearance = (): AppearanceContextValue => {
  const ctx = useContext(AppearanceContext);
  if (!ctx) {
    throw new Error("useAppearance must be used within an AppearanceProvider");
  }
  return ctx;
};
